import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { Copy, ShoppingBag, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";

const METHODS = [
  { id: "bkash", label: "bKash", color: "from-pink-500/20 to-pink-500/5", numKey: "bkash_number" },
  { id: "nagad", label: "Nagad", color: "from-orange-500/20 to-orange-500/5", numKey: "nagad_number" },
  { id: "rocket", label: "Rocket", color: "from-purple-500/20 to-purple-500/5", numKey: "rocket_number" },
];

export default function BuyPlan() {
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [paymentInfo, setPaymentInfo] = useState({});
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ method: "bkash", txn_id: "", payer_number: "", note: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data));
    api.get("/payment-info").then(({ data }) => setPaymentInfo(data));
  }, []);

  useEffect(() => {
    const buyId = params.get("buy");
    if (buyId && plans.length > 0) {
      const found = plans.find((p) => p.id === buyId);
      if (found) setSelected(found);
    }
  }, [params, plans]);

  const close = () => {
    setSelected(null);
    setForm({ method: "bkash", txn_id: "", payer_number: "", note: "" });
    if (params.get("buy")) {
      params.delete("buy");
      setParams(params, { replace: true });
    }
  };

  const submit = async () => {
    if (!form.txn_id.trim()) {
      toast.error("Transaction ID likhun");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/user/purchase", {
        plan_id: selected.id,
        method: form.method,
        txn_id: form.txn_id.trim(),
        payer_number: form.payer_number.trim(),
        note: form.note.trim(),
      });
      toast.success("Payment submitted. Admin verify kortese.");
      close();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const activeNumber = paymentInfo[METHODS.find((m) => m.id === form.method)?.numKey] || "—";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Buy a Plan</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Plan select korun, bKash/Nagad/Rocket diye pay korun, TXN ID submit korun.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className="glass-card rounded-lg p-5 flex flex-col" data-testid={`plan-${p.id}`}>
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
              {p.tool_name}
            </p>
            <h3 className="font-heading font-bold text-lg mt-1">{p.name}</h3>
            <div className="flex items-baseline gap-1 mt-3">
              <span className="font-heading text-3xl font-black tracking-tight">৳{p.price}</span>
              <span className="text-xs text-muted-foreground">/ {p.duration_days}d</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2 flex-1">{p.description}</p>
            <Button
              className="mt-4 w-full"
              onClick={() => setSelected(p)}
              data-testid={`buy-btn-${p.id}`}
            >
              <ShoppingBag className="w-4 h-4 mr-2" /> Buy Now
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && close()}>
        <DialogContent className="sm:max-w-2xl" data-testid="buy-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Pay ৳{selected?.price} for {selected?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wider">Payment Method</Label>
              <RadioGroup
                value={form.method}
                onValueChange={(v) => setForm({ ...form, method: v })}
                className="grid grid-cols-3 gap-3 mt-2"
              >
                {METHODS.map((m) => (
                  <label
                    key={m.id}
                    htmlFor={`method-${m.id}`}
                    className={`bg-gradient-to-br ${m.color} border rounded-lg p-4 cursor-pointer transition-colors ${
                      form.method === m.id ? "border-foreground" : "border-border"
                    }`}
                    data-testid={`method-${m.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-heading font-bold">{m.label}</p>
                      <RadioGroupItem value={m.id} id={`method-${m.id}`} />
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="rounded-md bg-secondary/40 p-4 space-y-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Send Money to this number
              </p>
              <div className="flex items-center justify-between">
                <code data-testid="merchant-number" className="font-mono text-base font-semibold">
                  {activeNumber}
                </code>
                <button
                  onClick={() => {
                    copyToClipboard(activeNumber);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="copy-number"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              {paymentInfo.instructions && (
                <p className="text-xs text-muted-foreground">{paymentInfo.instructions}</p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">Transaction ID *</Label>
                <Input
                  data-testid="txn-id"
                  placeholder="e.g. 9XY8Z..."
                  value={form.txn_id}
                  onChange={(e) => setForm({ ...form, txn_id: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider">Your {form.method} number</Label>
                <Input
                  data-testid="payer-number"
                  placeholder="01XXXXXXXXX"
                  value={form.payer_number}
                  onChange={(e) => setForm({ ...form, payer_number: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider">Note (optional)</Label>
              <Textarea
                data-testid="payer-note"
                rows={2}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={close} data-testid="buy-cancel">
              Cancel
            </Button>
            <Button onClick={submit} disabled={submitting} data-testid="buy-submit">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {submitting ? "Submitting…" : "Submit Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
